import {
  CellTemplate,
  Compatible,
  isAlphaNumericKey,
  keyCodes,
  Uncertain,
  UncertainCompatible,
} from "@silevis/reactgrid";
import { Styles } from "./Style";
import { Form } from "antd";
import React, { useEffect, useMemo, useRef } from "react";
import { CellCustomType } from "../../type";
import AttachmentField from "@lhd/components/CloneApp/fields/Attactment";
import { useRowHeight } from "@lhd/components/CloneApp/fields/TableSheet/RowHeightContext";
import { useSearchParams } from "react-router-dom";

function AttachmentCellRender({
  cell,
  isInEditMode,
  onCellChanged,
}: {
  cell: Compatible<CellCustomType>;
  isInEditMode: boolean;
  onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void;
}) {
  const [searchParams] = useSearchParams();
  const pageMode = searchParams.get("pageMode");
  const isViewMode = pageMode === "view";
  const timerIdChangedRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { updateRowHeight } = useRowHeight();
  const [form] = Form.useForm();
  const attachmentNamePath = useMemo(
    () => ["__table_attachment__"],
    [cell.appPart?.id, cell.clientId],
  );
  const watchedAttachment = Form.useWatch(attachmentNamePath, form);
  const prevWatchRef = useRef<string>("");
  const syncingFromInitRef = useRef(false);
  const normalizeAttachmentList = (value: unknown) =>
    Array.isArray(value) ? value : [];
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !cell.clientId || !cell.rowIndex) return;

    // ResizeObserver catches image load events (image finishes loading → container resizes)
    const observer = new ResizeObserver(() => {
      updateRowHeight(String(cell.clientId), cell.rowIndex as number);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [cell.clientId, cell.rowIndex, updateRowHeight]);

  const stopEvents = {
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
    onKeyUp: (e: React.KeyboardEvent) => e.stopPropagation(),
  };

  useEffect(() => {
    const current = normalizeAttachmentList(
      form.getFieldValue(attachmentNamePath),
    );
    prevWatchRef.current = JSON.stringify(current);

    if (current.length > 0) return;
    const initial = normalizeAttachmentList(cell.valueSubmitAttachment);
    if (initial.length === 0) return;

    syncingFromInitRef.current = true;
    form.setFieldValue(attachmentNamePath, initial);
  }, [attachmentNamePath, cell.valueSubmitAttachment, form]);

  useEffect(() => {
    const nextValue = normalizeAttachmentList(watchedAttachment);
    const nextSerialized = JSON.stringify(nextValue);

    if (syncingFromInitRef.current) {
      syncingFromInitRef.current = false;
      prevWatchRef.current = nextSerialized;
      return;
    }

    if (prevWatchRef.current === nextSerialized) return;
    prevWatchRef.current = nextSerialized;
    timerIdChangedRef.current && clearTimeout(timerIdChangedRef.current);
    timerIdChangedRef.current = setTimeout(() => {
      onCellChanged(
        {
          ...cell,
          text: Date.now() + "", // force update
          valueSubmitAttachment: nextValue,
        },
        true,
      );
    }, 100);
  }, [cell, onCellChanged, watchedAttachment]);

  return (
    <Form form={form} style={{ minWidth: 0, width: "100%" }}>
      <Styles
        onClick={(e) => {
          if (isInEditMode) e.stopPropagation();
        }}
        onPointerDown={(e) => {
          if (isInEditMode) e.stopPropagation();
        }}
        onMouseDown={(e) => {
          if (isInEditMode) e.stopPropagation();
        }}
      >
        <div ref={containerRef} {...stopEvents}>
          <AttachmentField
            namePath={attachmentNamePath}
            item={cell.appPart}
            isDisabled={!!cell.nonEditable || isViewMode}
            isTable={true}
          />
        </div>
      </Styles>
    </Form>
  );
}

export class AttachmentCellTemplate implements CellTemplate<CellCustomType> {
  getCompatibleCell(
    uncertainCell: Uncertain<CellCustomType>,
  ): Compatible<CellCustomType> {
    const uncertainCellAs = uncertainCell as CellCustomType;
    return {
      value: NaN,
      ...uncertainCellAs,
    };
  }

  getClassName(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
  ): string {
    return cell.className || "";
  }

  update(
    cell: Compatible<CellCustomType>,
    cellToMerge: UncertainCompatible<CellCustomType>,
  ): Compatible<CellCustomType> {
    const merge = cellToMerge as CellCustomType;
    return this.getCompatibleCell({
      ...cell,
      valueSubmit: cellToMerge.valueSubmit,
      ...(merge.valueSubmitAttachment !== undefined
        ? { valueSubmitAttachment: merge.valueSubmitAttachment }
        : {}),
    });
  }

  handleKeyDown(
    cell: Compatible<CellCustomType>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<CellCustomType>; enableEditMode: boolean } {
    if (keyCode === keyCodes.ENTER && shift) {
      return { cell, enableEditMode: true };
    }
    if (!ctrl && !alt && isAlphaNumericKey(keyCode)) {
      return { cell, enableEditMode: true };
    }
    return {
      cell,
      enableEditMode:
        keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
    };
  }

  render(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void,
  ) {
    return (
      <AttachmentCellRender
        cell={cell}
        isInEditMode={isInEditMode}
        onCellChanged={onCellChanged}
      />
    );
  }
}
